import React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";

import "../redesign/contactRedesign.css";
import {
  AnlagenImportBericht,
  AnlagenVorschau,
  KontoBefund,
  KontoRegel,
  anlagenImportService,
} from "../../services/anlagenImportService";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Anlagenverzeichnis aus einem DATEV-Export einspielen.
 *
 * Drei Schritte wie im Excel-Import (`pages/ImportPage.tsx`): hochladen,
 * prüfen, übernehmen. Der Unterschied liegt im mittleren Schritt — entschieden
 * wird **je Konto**, nicht je Zeile. Aus 169 Positionen werden acht
 * Entscheidungen, und genau die acht sind die, die Fachwissen brauchen.
 *
 * Ein Konto ohne hinterlegte Zuordnung blockiert die Übernahme. Das ist der
 * Kern: Lieber eine offene Zeile, die jemand füllen muss, als eine Position,
 * die still im falschen Block landet.
 *
 * ── Optik ────────────────────────────────────────────────────────────────
 * Das Refined-Modal des Hauses (`.kvp-rd-scrim > .dm` aus `contactRedesign.css`,
 * portaliert nach `document.body`), NICHT der Fluent-Dialog. Der Kontakt-Reiter
 * darunter ist durchgehend in diesem Design gehalten; ein Fluent-`DialogSurface`
 * brachte eigene Radien, Schatten, Schriftgrößen und Knöpfe mit und sah aus wie
 * aus einer anderen Anwendung. Bausteine: `.dm-head`/`.dm-body`/`.dm-foot`,
 * `.fld` + `.inp` für Eingaben, `.card` + `table.kt` für Tabellen,
 * `.rd-pop-btn` für Knöpfe. Die Teile, die es dort noch nicht gab
 * (Schrittleiste, Ablagefläche, Hinweiskästen), stehen als `.av-*` am Ende
 * derselben CSS.
 */

/**
 * Aus dem riskType folgt die Objektkategorie. Zwei Felder, die auseinanderlaufen
 * können, wären eine Fehlerquelle ohne Nutzen — im Dialog wird deshalb nur der
 * riskType gewählt.
 */
const KATEGORIE_ZU: Record<KontoRegel["riskType"], string> = {
  "Betriebsvermögen": "Betriebsvermögen",
  Maschine: "Maschine",
  Immobilie: "Immobilien",
  KFZ: "KFZ",
};

const RISK_TYPES = Object.keys(KATEGORIE_ZU) as KontoRegel["riskType"][];

/** Auswahlwert für „kein Neuwert" — von echten Sparten unterscheidbar. */
const OHNE_NEUWERT = "__ohne__";

/** Platzhalter einer noch nicht getroffenen Wahl. */
const OFFEN = "";

const eur = (v: number) =>
  v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

// Linien-Icons im Stil von `redesign/kvpIcons.tsx` — bewusst lokal und klein
// gehalten, statt @fluentui/react-icons in eine ansonsten Fluent-freie
// Komponente zurückzuholen.
const IcoUpload = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13.5V4M6.5 7.5L10 4l3.5 3.5" />
    <path d="M3.5 13v2.5a1 1 0 001 1h11a1 1 0 001-1V13" />
  </svg>
);
const IcoX = (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
    <path d="M5 5l10 10M15 5L5 15" />
  </svg>
);
const IcoWarn = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 3.2l7 12.3H3z" />
    <path d="M10 8v3.4M10 13.6v.1" />
  </svg>
);
const IcoCheck = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="7" />
    <path d="M6.8 10.2l2.2 2.2 4.2-4.6" />
  </svg>
);

type Schritt = "upload" | "pruefen" | "fertig";

const SCHRITTE: { id: Schritt; label: string }[] = [
  { id: "upload", label: "1 · Hochladen" },
  { id: "pruefen", label: "2 · Prüfen & Zuordnen" },
  { id: "fertig", label: "3 · Übernehmen" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  kontaktId: number;
  kontaktName: string;
  /** Wird nach erfolgreicher Übernahme gerufen, damit die Matrix neu lädt. */
  onFertig?: () => void;
}

const AnlagenImportDialog: React.FC<Props> = ({ open, onClose, kontaktId, kontaktName, onFertig }) => {
  const [schritt, setSchritt] = React.useState<Schritt>("upload");
  const [datei, setDatei] = React.useState<File | null>(null);
  const [ueberAblage, setUeberAblage] = React.useState(false);
  const [praefix, setPraefix] = React.useState("");
  const [risikoort, setRisikoort] = React.useState("");
  const [laeuft, setLaeuft] = React.useState(false);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [vorschau, setVorschau] = React.useState<AnlagenVorschau | null>(null);
  const [regeln, setRegeln] = React.useState<Record<string, KontoRegel>>({});
  const [bericht, setBericht] = React.useState<AnlagenImportBericht | null>(null);
  const dateiFeld = React.useRef<HTMLInputElement>(null);

  const zuruecksetzen = () => {
    setSchritt("upload");
    setDatei(null);
    setVorschau(null);
    setRegeln({});
    setBericht(null);
    setFehler(null);
  };

  const schliessen = React.useCallback(() => {
    zuruecksetzen();
    onClose();
  }, [onClose]);

  // Escape schließt — beim Fluent-Dialog kam das mit der Komponente, hier ist es
  // Handarbeit. Während eines laufenden Vorgangs NICHT: ein halb geschriebener
  // Import ließe sich sonst mit einem Tastendruck aus dem Blick nehmen.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !laeuft) schliessen();
    };
    alleFenster.addEventListener("keydown", onKey);
    return () => alleFenster.removeEventListener("keydown", onKey);
  }, [open, laeuft, schliessen]);

  const dateiWaehlen = (f: File | null) => {
    setDatei(f);
    setFehler(null);
    // Präfix aus dem Firmennamen vorschlagen: Initialen der ersten drei Wörter.
    if (f && !praefix) {
      const initialen = kontaktName
        .replace(/[^A-Za-zÄÖÜäöüß ]/g, " ")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 3)
        .map((w) => w[0]?.toUpperCase() ?? "")
        .join("");
      if (initialen.length >= 2) setPraefix(initialen);
    }
  };

  const hochladen = async () => {
    if (!datei) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const v = await anlagenImportService.vorschau(datei, kontaktId);
      setVorschau(v);
      // Die Vorschläge als Startwert — geändert wird nur, was jemand anfasst.
      const start: Record<string, KontoRegel> = {};
      for (const k of v.konten) if (k.vorschlag) start[k.konto] = k.vorschlag;
      setRegeln(start);
      setSchritt("pruefen");
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const uebernehmen = async () => {
    if (!vorschau) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const b = await anlagenImportService.uebernehmen({
        token: vorschau.token,
        kontaktId,
        praefix,
        risikoort: risikoort.trim() || undefined,
        jahr: vorschau.zieljahr,
        zuordnungen: regeln,
      });
      setBericht(b);
      setSchritt("fertig");
      onFertig?.();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  /** Sparte eines Kontos ändern — Kategorie und Vermögensart bleiben bestehen. */
  const setzeSparte = (k: KontoBefund, wert: string) => {
    const bisher = regeln[k.konto] ?? k.vorschlag;
    const basis: KontoRegel = bisher ?? {
      riskType: "Betriebsvermögen",
      kategorie: KATEGORIE_ZU["Betriebsvermögen"],
      vermoegensart: k.kontobezeichnung || `Konto ${k.konto}`,
      indexsparte: null,
      ausschlussgrund: null,
    };
    setRegeln({
      ...regeln,
      [k.konto]:
        wert === OHNE_NEUWERT
          ? {
              ...basis,
              indexsparte: null,
              ausschlussgrund:
                basis.ausschlussgrund ?? "Für dieses Konto wird bei diesem Import kein Neuwert gerechnet.",
            }
          : { ...basis, indexsparte: wert, ausschlussgrund: null },
    });
  };

  const setzeRiskType = (k: KontoBefund, wert: KontoRegel["riskType"]) => {
    const basis = regeln[k.konto] ?? k.vorschlag;
    setRegeln({
      ...regeln,
      [k.konto]: {
        riskType: wert,
        kategorie: KATEGORIE_ZU[wert],
        vermoegensart: basis?.vermoegensart || k.kontobezeichnung || `Konto ${k.konto}`,
        indexsparte: basis?.indexsparte ?? null,
        ausschlussgrund:
          basis?.indexsparte == null
            ? (basis?.ausschlussgrund ?? "Für dieses Konto wird bei diesem Import kein Neuwert gerechnet.")
            : null,
      },
    });
  };

  /** Offen ist ein Konto, für das noch gar keine Regel steht. */
  const nochOffen = React.useMemo(
    () => (vorschau ? vorschau.konten.filter((k) => !regeln[k.konto]).map((k) => k.konto) : []),
    [vorschau, regeln],
  );

  const praefixOk = /^[A-Za-z0-9-]{2,12}$/.test(praefix);

  if (!open) return null;

  const schrittIndex = SCHRITTE.findIndex((s) => s.id === schritt);

  const fuss = (() => {
    if (schritt === "upload")
      return (
        <>
          <button type="button" className="rd-pop-btn" onClick={schliessen} disabled={laeuft}>
            Abbrechen
          </button>
          <button
            type="button"
            className="rd-pop-btn primary"
            disabled={!datei || !praefixOk || laeuft}
            onClick={hochladen}
          >
            {laeuft ? "Wird gelesen …" : "Lesen und prüfen"}
          </button>
        </>
      );
    if (schritt === "pruefen")
      return (
        <>
          <button
            type="button"
            className="rd-pop-btn"
            onClick={() => setSchritt("upload")}
            disabled={laeuft}
          >
            Zurück
          </button>
          <button
            type="button"
            className="rd-pop-btn primary"
            disabled={nochOffen.length > 0 || laeuft}
            onClick={uebernehmen}
          >
            {laeuft ? "Wird übernommen …" : `${vorschau?.positionen ?? 0} Positionen übernehmen`}
          </button>
        </>
      );
    return (
      <button type="button" className="rd-pop-btn primary" onClick={schliessen}>
        Schließen
      </button>
    );
  })();

  return createPortal(
    <div className="kvp-rd kvp-rd-scrim" onClick={laeuft ? undefined : schliessen}>
      <div
        className="dm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="anlagen-import-titel"
        style={{ width: "min(1060px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-ico">{IcoUpload}</div>
            <div className="dm-titles">
              <div className="dm-kicker">Betriebs- und Anlagevermögen</div>
              <h2 id="anlagen-import-titel">Anlagenverzeichnis einspielen</h2>
            </div>
            <button
              className="dm-x"
              type="button"
              onClick={schliessen}
              disabled={laeuft}
              aria-label="Schließen"
            >
              {IcoX}
            </button>
          </div>
          {/* Keine Reiter, sondern eine Reihenfolge: Ein Sprung nach vorn wäre
              ohne gelesene Datei sinnlos, ein Sprung zurück ist der
              „Zurück"-Knopf. Die Leiste zeigt nur, wo man steht. */}
          <ol className="av-steps">
            {SCHRITTE.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 && <li className="av-steps-sep" aria-hidden>›</li>}
                <li
                  className={s.id === schritt ? "on" : i < schrittIndex ? "done" : undefined}
                  aria-current={s.id === schritt ? "step" : undefined}
                >
                  {s.label}
                </li>
              </React.Fragment>
            ))}
          </ol>
        </div>

        <div className="dm-body" style={{ display: "grid", gap: 16 }}>
          {fehler && <div className="av-err">{fehler}</div>}

          {/* ── Schritt 1 ───────────────────────────────────────────────── */}
          {schritt === "upload" && (
            <>
              <div
                className={`av-drop${ueberAblage ? " over" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setUeberAblage(true);
                }}
                onDragLeave={() => setUeberAblage(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setUeberAblage(false);
                  dateiWaehlen(e.dataTransfer.files?.[0] ?? null);
                }}
                onClick={() => dateiFeld.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    dateiFeld.current?.click();
                  }
                }}
              >
                <span className="av-drop-ico">{IcoUpload}</span>
                <div className="av-drop-name">
                  {datei ? datei.name : "DATEV-Export ablegen oder auswählen"}
                </div>
                <div className="av-drop-sub">
                  „Entwicklung des Anlagevermögens" als .xlsx — der Rohexport, keine aufbereitete
                  Mappe.
                </div>
                <input
                  ref={dateiFeld}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={(e) => dateiWaehlen(e.target.files?.[0] ?? null)}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14 }}>
                <div className="fld">
                  <label htmlFor="anlagen-praefix">Kennung (Präfix)</label>
                  <input
                    id="anlagen-praefix"
                    className={`inp${praefix && !praefixOk ? " invalid" : ""}`}
                    value={praefix}
                    onChange={(e) => setPraefix(e.target.value)}
                    placeholder="z. B. CCL"
                  />
                  <span className="av-kopfzeile">
                    Steht vor jeder Positionsnummer: {praefix || "CCL"}-135003. Über sie erkennt ein
                    zweiter Lauf, was schon da ist.
                  </span>
                </div>
                <div className="fld">
                  <label htmlFor="anlagen-risikoort">Risikoort (optional)</label>
                  <input
                    id="anlagen-risikoort"
                    className="inp"
                    value={risikoort}
                    onChange={(e) => setRisikoort(e.target.value)}
                    placeholder="Straße, PLZ Ort"
                  />
                  <span className="av-kopfzeile">Wird an jede Position geschrieben.</span>
                </div>
              </div>
            </>
          )}

          {/* ── Schritt 2 ───────────────────────────────────────────────── */}
          {schritt === "pruefen" && vorschau && (
            <>
              <div className="av-kopfzeile">
                {vorschau.dateiname} · {vorschau.positionen} Positionen · {vorschau.kontakt} ·
                Indexstand {vorschau.zieljahr}
              </div>

              {nochOffen.length > 0 && (
                <div className="av-note warn">
                  {IcoWarn}
                  <span>
                    Für {nochOffen.length === 1 ? "das Konto" : "die Konten"}{" "}
                    <strong>{nochOffen.join(", ")}</strong> ist keine Zuordnung hinterlegt. Ohne sie
                    landete die Position im falschen Block und mit dem falschen Index — bitte unten
                    festlegen.
                  </span>
                </div>
              )}

              <div className="card">
                <table className="kt av-kt">
                  <thead>
                    <tr>
                      <th>Konto</th>
                      <th>Bezeichnung</th>
                      <th className="av-num">Pos.</th>
                      <th className="av-num">AHK</th>
                      <th>Führt als</th>
                      <th>Index-Sparte</th>
                      <th className="av-num">Neuwert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vorschau.konten.map((k) => {
                      const regel = regeln[k.konto];
                      const offen = !regel;
                      return (
                        <tr key={k.konto} className={offen ? "av-offen" : undefined}>
                          <td className="name">{k.konto}</td>
                          <td>{k.kontobezeichnung}</td>
                          <td className="av-num">{k.positionen}</td>
                          <td className="av-num">{eur(k.ahk)}</td>
                          <td>
                            <select
                              className={`av-sel${offen ? " leer" : ""}`}
                              value={regel?.riskType ?? OFFEN}
                              onChange={(e) =>
                                setzeRiskType(k, e.target.value as KontoRegel["riskType"])
                              }
                              aria-label={`Führt als — Konto ${k.konto}`}
                            >
                              <option value={OFFEN} disabled>
                                wählen
                              </option>
                              {RISK_TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className={`av-sel${offen ? " leer" : ""}`}
                              value={regel ? (regel.indexsparte ?? OHNE_NEUWERT) : OFFEN}
                              onChange={(e) => setzeSparte(k, e.target.value)}
                              aria-label={`Index-Sparte — Konto ${k.konto}`}
                            >
                              <option value={OFFEN} disabled>
                                wählen
                              </option>
                              {vorschau.sparten.map((sp) => (
                                <option key={sp} value={sp}>
                                  {sp}
                                </option>
                              ))}
                              <option value={OHNE_NEUWERT}>kein Neuwert</option>
                            </select>
                            {k.fehler && !offen && <div className="av-zellfehler">{k.fehler}</div>}
                          </td>
                          <td className="av-num">
                            {regel && regel.indexsparte === null
                              ? "—"
                              : k.neuwert !== null
                                ? eur(k.neuwert)
                                : "wird beim Übernehmen gerechnet"}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="av-sum">
                      <td colSpan={3}>GESAMT</td>
                      <td className="av-num">{eur(vorschau.ahkGesamt)}</td>
                      <td colSpan={2} />
                      <td className="av-num">{eur(vorschau.neuwertGesamt)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="av-note">
                <span>
                  Konten ohne Index-Sparte bekommen <strong>keine</strong> Versicherungssumme — nicht
                  0 €. Bei Kfz, geringwertigen Wirtschaftsgütern und Anzahlungen ist das so gewollt:
                  Dort gilt eine andere Systematik, und eine 0 wäre eine Aussage über den Wert.
                </span>
              </div>

              {vorschau.hinweise.map((h) => (
                <div key={h} className="av-note">
                  <span>{h}</span>
                </div>
              ))}
            </>
          )}

          {/* ── Schritt 3 ───────────────────────────────────────────────── */}
          {schritt === "fertig" && bericht && (
            <>
              <div className="av-note ok">
                {IcoCheck}
                <span>
                  <strong>{bericht.angelegt}</strong> Positionen angelegt
                  {bericht.uebersprungen > 0 && (
                    <>
                      , <strong>{bericht.uebersprungen}</strong> waren bereits vorhanden und blieben
                      unverändert
                    </>
                  )}
                  .
                </span>
              </div>

              <div className="card">
                <table className="kt av-kt">
                  <thead>
                    <tr>
                      <th>Konto</th>
                      <th>Bezeichnung</th>
                      <th className="av-num">Pos.</th>
                      <th className="av-num">AHK</th>
                      <th className="av-num">Neuwert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bericht.konten.map((k) => (
                      <tr key={k.konto}>
                        <td className="name">{k.konto}</td>
                        <td>{k.bezeichnung}</td>
                        <td className="av-num">{k.positionen}</td>
                        <td className="av-num">{eur(k.ahk)}</td>
                        <td className="av-num">
                          {k.neuwert === null ? "ohne Neuwert" : eur(k.neuwert)}
                        </td>
                      </tr>
                    ))}
                    <tr className="av-sum">
                      <td colSpan={3}>GESAMT</td>
                      <td className="av-num">{eur(bericht.ahkGesamt)}</td>
                      <td className="av-num">{eur(bericht.neuwertGesamt)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="av-note">
                <span>
                  Ohne Neuwert: <strong>{bericht.ohneNeuwert}</strong> Positionen,{" "}
                  {eur(bericht.ahkOhneNeuwert)} AHK — das sind{" "}
                  {bericht.ahkGesamt > 0
                    ? ((bericht.ahkOhneNeuwert / bericht.ahkGesamt) * 100).toFixed(1)
                    : "0"}{" "}
                  % des Buchbestands.
                </span>
              </div>
            </>
          )}
        </div>

        <div className="dm-foot">{fuss}</div>
      </div>
    </div>,
    document.body,
  );
};

export default AnlagenImportDialog;
